import { getNinjaConfig } from '@/config/ninja';
import { getRegionConfig } from '@/config/region';
import { GENERAL_EVENTS } from '@/const/events';
import { ninjaBus } from '@/eventbus';
import { AVAILABLE_TRACKERS, Tracker } from '@/generated/types.generated.ts';
import { getLogger } from '@/logger';
import { TASK_PRIORITY, taskQueue } from '@/task-queue';
import { Event, EventHandler, UserProps } from '@/types/events';
import { hasRequiredConsentFor } from '@/utils/consent';
import { getTrackParams } from './params';

const logger = getLogger('BaseTracker');

export interface Inittable {
  init(): Promise<unknown>;
}

export interface Setupable {
  listeners: Record<string, EventHandler>;
  setupListeners(): Promise<unknown>;
}

/**
 * Possible states:
 * - not_loaded: tracker is not loaded, init has not been called yet
 * - loading: init has been called, tracker is loading. Queue events until loaded or error state is reached, then flush
 * - ready: tracker is loaded and ready to use, directly track events
 * - error: error occurred during loading or initialization, do not track
 * - disabled: tracker is no longer in use, do not track
 */
export type TrackerState = 'not_loaded' | 'loading' | 'ready' | 'error' | 'disabled';

export abstract class BaseTracker implements Inittable, Setupable {
  state: TrackerState = 'not_loaded';
  loadingQueue: (() => Promise<any>)[] = [];

  listeners: Record<string, EventHandler> = {};
  trackerName: Tracker;
  scriptTagId?: string;
  scriptUrl?: string;
  private isListening = false;

  abstract init(): Promise<unknown>;
  abstract trackEvent(e: Event, isLinkEvent?: boolean): Promise<void>;
  abstract trackPage(e: Event): Promise<void>;

  protected createStandardTrackingListeners(): Record<string, EventHandler> {
    return {
      trackEvent: e => {
        this.initAndExecute(async () => this.trackEvent(e));
      },
      trackPage: e => {
        this.initAndExecute(async () => this.trackPage(e));
      },
      trackLinkEvent: e => {
        this.initAndExecute(async () => this.trackLinkEvent(e));
      },
    };
  }

  protected shouldTrackEvent(e: Event): boolean {
    if (this.isDisabled()) {
      this.cleanup();
      logger.debug(`Tracker "${this.trackerName}" is in ${this.state} state. Event tracking aborted`, e);
      return false;
    }
    return true;
  }

  async setupListeners() {
    if (!this.isListening) {
      for (const [event, callback] of Object.entries(this.listeners)) {
        ninjaBus.off(event, callback);
        ninjaBus.on(event, callback);
      }
      logger.debug(`${this.trackerName} tracker callbacks set up`);
      this.isListening = true;
    }

    return this;
  }

  cleanup() {
    this.state = 'disabled';
  }

  isDisabled() {
    return this.state === 'disabled' || this.state === 'error' || !this.hasRequiredConsent();
  }

  async trackLinkEvent(e: Event) {
    ninjaBus.emit(GENERAL_EVENTS.TRACK_FEATURE_USAGE, '', { message: 'trackers/trackLinkEvent' });
    return this.trackEvent(e, true);
  }

  /**
   * Get token for a tracker from the region config
   */
  getToken() {
    return getRegionConfig()?.custom?.[getNinjaConfig().siteUrl]?.config?.[AVAILABLE_TRACKERS[this.trackerName]]?.code;
  }

  /**
   * Get single object containing custom, ninja default and tracker-specific params.
   * They are encoded and mapped to the tracker's matrix
   */
  getTrackParams(event: Event, additionalParams: UserProps = {}) {
    return getTrackParams(this.trackerName, event, additionalParams);
  }

  /**
   * Helper function to manage tracker state and ensure events are tracked in the right order, even if the tracker is not ready yet.
   * Should be used only for main tracker functionality
   */
  initAndExecute(fn: () => Promise<any>) {
    taskQueue.enqueue({
      fn: async () => {
        await this.setupListeners();

        switch (this.state) {
          case 'not_loaded':
            logger.debug(`${this.trackerName} initializing`);
            this.state = 'loading';
            this.loadingQueue.push(fn);
            await this.init();
            break;

          case 'loading':
            this.loadingQueue.push(fn);
            logger.debug(`${this.trackerName} is loading. Event added to the loading queue. Queue length: ${this.loadingQueue.length}`);
            break;

          case 'ready':
            // ensure events are executed in order they were called, even if tracker is ready but there are still events in the loading queue
            if (this.loadingQueue.length > 0) {
              this.loadingQueue.push(fn);
            } else {
              await fn();
            }
            break;

          case 'error':
          case 'disabled':
            logger.debug(`${this.trackerName} is in state ${this.state}, not tracking event`);
            break;
        }

        if (this.state === 'ready' && this.loadingQueue.length > 0) {
          logger.debug(`${this.trackerName} processing loading queue`);

          for (const callbackFn of this.loadingQueue) {
            await callbackFn();
          }

          this.loadingQueue = [];
        }
      },
      priority: TASK_PRIORITY.HIGH,
      taskId: taskQueue.generateTaskId(`initAndExecute_${this.trackerName}`),
    });
  }

  hasRequiredConsent() {
    return hasRequiredConsentFor(this.trackerName);
  }
}
